/**
 * Opt-in element screenshot via the browser's native screen-capture API.
 *
 * Flow:
 *   1. Ask for `getDisplayMedia` with `preferCurrentTab` if supported.
 *   2. Browser shows its standard share-picker — the USER chooses what to
 *      share. No silent capture, no third-party canvas. Privacy-safe.
 *   3. We grab a single video frame, crop to the element's
 *      `getBoundingClientRect()` accounting for `devicePixelRatio`, and
 *      stop the stream.
 *   4. Return a PNG data URL.
 *
 * Returns `null` if the user denies the picker, the API is unavailable,
 * or anything throws.
 */
const DEFAULTS = {
    maxWidth: 1200,
    quality: 0.9,
    mimeType: "image/png",
};
const stopStream = (stream) => {
    for (const track of stream.getTracks()) {
        try {
            track.stop();
        }
        catch {
            /* ignore */
        }
    }
};
const grabFrame = async (stream) => {
    const video = document.createElement("video");
    video.srcObject = stream;
    video.muted = true;
    video.playsInline = true;
    try {
        await video.play();
    }
    catch {
        return null;
    }
    // Wait a few frames so the video has dimensions.
    await new Promise((resolve) => {
        if (video.readyState >= 2) {
            resolve();
            return;
        }
        const onLoaded = () => {
            video.removeEventListener("loadeddata", onLoaded);
            resolve();
        };
        video.addEventListener("loadeddata", onLoaded);
    });
    const w = video.videoWidth;
    const h = video.videoHeight;
    if (w === 0 || h === 0)
        return null;
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx)
        return null;
    ctx.drawImage(video, 0, 0, w, h);
    return canvas;
};
const cropTo = (source, rect, dpr, maxWidth) => {
    const sx = Math.max(0, Math.round(rect.x * dpr));
    const sy = Math.max(0, Math.round(rect.y * dpr));
    const sw = Math.min(source.width - sx, Math.max(1, Math.round(rect.width * dpr)));
    const sh = Math.min(source.height - sy, Math.max(1, Math.round(rect.height * dpr)));
    if (sw <= 0 || sh <= 0)
        return null;
    const scale = sw > maxWidth ? maxWidth / sw : 1;
    const dw = Math.max(1, Math.round(sw * scale));
    const dh = Math.max(1, Math.round(sh * scale));
    const out = document.createElement("canvas");
    out.width = dw;
    out.height = dh;
    const ctx = out.getContext("2d");
    if (!ctx)
        return null;
    ctx.drawImage(source, sx, sy, sw, sh, 0, 0, dw, dh);
    return out;
};
export const isScreenshotSupported = () => typeof navigator !== "undefined" &&
    !!navigator.mediaDevices &&
    typeof navigator.mediaDevices.getDisplayMedia === "function";
export const captureElementScreenshot = async (element, options = {}) => {
    if (!isScreenshotSupported())
        return null;
    const opts = { ...DEFAULTS, ...options };
    const rect = element.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0)
        return null;
    let stream = null;
    try {
        const displayOptions = {
            video: { displaySurface: "browser" },
            audio: false,
            preferCurrentTab: true,
            selfBrowserSurface: "include",
        };
        stream = await navigator.mediaDevices.getDisplayMedia(displayOptions);
    }
    catch {
        return null;
    }
    try {
        const full = await grabFrame(stream);
        if (!full)
            return null;
        const dpr = window.devicePixelRatio || 1;
        const cropped = cropTo(full, rect, dpr, opts.maxWidth);
        if (!cropped)
            return null;
        return cropped.toDataURL(opts.mimeType, opts.quality);
    }
    catch {
        return null;
    }
    finally {
        if (stream)
            stopStream(stream);
    }
};
export const captureElementOuterHTML = (element, maxChars = 4000) => {
    try {
        const html = element.outerHTML ?? "";
        return html.length <= maxChars
            ? html
            : html.slice(0, maxChars - 1) + "…";
    }
    catch {
        return "";
    }
};
//# sourceMappingURL=screenshot.js.map